import type { PickupHandoffModeV3 } from 'pi-kiosk-shared';

const SELF_HANDOFF_MODES = new Set<PickupHandoffModeV3>(['CUSTOMER_TAP', 'AUTO_ON_PAYMENT']);

const SELF_HANDOFF_PRIORITY: readonly PickupHandoffModeV3[] = ['CUSTOMER_TAP', 'AUTO_ON_PAYMENT'];
const BAR_HANDOFF_PRIORITY: readonly PickupHandoffModeV3[] = ['STAFF_SCAN', 'SCAN_AT_STAND'];

export type RetailHandoffChoiceKind = 'self' | 'bar';

export interface RetailHandoffChoice {
  readonly key: RetailHandoffChoiceKind;
  readonly kind: RetailHandoffChoiceKind;
  readonly mode: PickupHandoffModeV3;
}

export function isSelfPickupHandoffMode(mode: PickupHandoffModeV3): boolean {
  return SELF_HANDOFF_MODES.has(mode);
}

function pickRepresentativeMode(
  modes: readonly PickupHandoffModeV3[],
  priority: readonly PickupHandoffModeV3[],
): PickupHandoffModeV3 | undefined {
  if (modes.length === 0) {
    return undefined;
  }
  for (const preferred of priority) {
    if (modes.includes(preferred)) {
      return preferred;
    }
  }
  return modes[0];
}

/** Collapse capability modes into at most one self + one bar card for retail collect UI. */
export function resolveRetailHandoffChoices(
  allowedModes: readonly PickupHandoffModeV3[],
): readonly RetailHandoffChoice[] {
  const selfModes = allowedModes.filter((mode) => isSelfPickupHandoffMode(mode));
  const barModes = allowedModes.filter((mode) => !isSelfPickupHandoffMode(mode));
  const choices: RetailHandoffChoice[] = [];

  const selfMode = pickRepresentativeMode(selfModes, SELF_HANDOFF_PRIORITY);
  if (selfMode !== undefined) {
    choices.push({ key: 'self', kind: 'self', mode: selfMode });
  }

  const barMode = pickRepresentativeMode(barModes, BAR_HANDOFF_PRIORITY);
  if (barMode !== undefined) {
    choices.push({ key: 'bar', kind: 'bar', mode: barMode });
  }

  return choices;
}

export function isRetailHandoffChoiceSelected(
  choice: RetailHandoffChoice,
  effectiveHandoffMode: PickupHandoffModeV3 | null,
): boolean {
  if (effectiveHandoffMode === null) {
    return false;
  }
  if (choice.kind === 'self') {
    return isSelfPickupHandoffMode(effectiveHandoffMode);
  }
  return !isSelfPickupHandoffMode(effectiveHandoffMode);
}
